// src/Pages/Clients/Sesiones/DriveFolderPicker.js
// Modal para elegir en que carpeta del Drive del cliente queda la sesion.
//
// Solo se listan carpetas, nunca archivos. Con el scope `drive` el token tiene
// acceso a todo el Drive de la persona, asi que lo que este componente pide es
// deliberadamente lo mas chico posible.
import React, { useCallback, useEffect, useState } from "react";
import {
  Folder,
  ChevronRight,
  ArrowLeft,
  Check,
  X,
  Loader2,
  TriangleAlert,
} from "lucide-react";
import { listarCarpetas } from "../../../utils/driveService";

const RAIZ = { id: null, name: "Mi Drive" };

const DriveFolderPicker = ({ abierto, onClose, onElegir }) => {
  const [ruta, setRuta] = useState([RAIZ]);
  const [carpetas, setCarpetas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);

  const actual = ruta[ruta.length - 1];

  const cargar = useCallback(async (parentId) => {
    setCargando(true);
    setError(null);
    try {
      const { carpetas: lista } = await listarCarpetas(parentId);
      setCarpetas(Array.isArray(lista) ? lista : []);
    } catch (err) {
      setCarpetas([]);
      setError(err.message || String(err));
    } finally {
      setCargando(false);
    }
  }, []);

  // Cada vez que se abre arranca en la raiz: reabrir en el nivel donde quedo
  // la vez anterior confunde mas de lo que ayuda.
  useEffect(() => {
    if (!abierto) return;
    setRuta([RAIZ]);
    setError(null);
    cargar(null);
  }, [abierto, cargar]);

  useEffect(() => {
    if (!abierto) return;
    const onEscape = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onEscape);
    return () => document.removeEventListener("keydown", onEscape);
  }, [abierto, onClose]);

  if (!abierto) return null;

  const entrar = (carpeta) => {
    setRuta((r) => [...r, carpeta]);
    cargar(carpeta.id);
  };

  const volver = () => {
    setRuta((r) => r.slice(0, -1));
    const padre = ruta[ruta.length - 2];
    cargar(padre?.id ?? null);
  };

  const confirmar = () => onElegir({ id: actual.id, name: actual.name });

  return (
    <div
      className="sesiones-drive-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Elegir carpeta de Google Drive"
      onClick={onClose}
    >
      <div
        className="sesiones-drive-modal-box"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sesiones-drive-modal-head">
          <h3 className="sesiones-drive-modal-title">Elegí una carpeta</h3>
          <button
            type="button"
            className="sesiones-drive-modal-close"
            onClick={onClose}
            aria-label="Cerrar"
          >
            <X size={20} />
          </button>
        </header>

        {/* Migas: donde estas parado en el Drive */}
        <nav className="sesiones-drive-crumbs" aria-label="Ruta de carpetas">
          {ruta.length > 1 && (
            <button
              type="button"
              className="sesiones-drive-back"
              onClick={volver}
              aria-label="Volver a la carpeta anterior"
            >
              <ArrowLeft size={16} />
            </button>
          )}
          {ruta.map((p, i) => (
            <React.Fragment key={`${p.id ?? "root"}-${i}`}>
              {i > 0 && <ChevronRight size={14} className="sesiones-drive-crumb-sep" />}
              <button
                type="button"
                className={`sesiones-drive-crumb${
                  i === ruta.length - 1 ? " sesiones-drive-crumb--actual" : ""
                }`}
                onClick={() => {
                  const destino = ruta.slice(0, i + 1);
                  setRuta(destino);
                  cargar(destino[destino.length - 1].id);
                }}
              >
                {p.name}
              </button>
            </React.Fragment>
          ))}
        </nav>

        <div className="sesiones-drive-list">
          {cargando && (
            <p className="sesiones-drive-list-msg">
              <Loader2 size={16} className="sesiones-spin" /> Cargando carpetas...
            </p>
          )}

          {!cargando && error && (
            <p className="sesiones-drive-list-msg sesiones-drive-list-msg--error">
              <TriangleAlert size={16} /> {error}
            </p>
          )}

          {!cargando && !error && carpetas.length === 0 && (
            <p className="sesiones-drive-list-msg">
              Acá no hay carpetas. Podés crear una desde Google Drive y volver a
              intentar.
            </p>
          )}

          {!cargando &&
            !error &&
            carpetas.map((c) => (
              <button
                type="button"
                key={c.id}
                className="sesiones-drive-item"
                onClick={() => entrar(c)}
              >
                <Folder size={18} />
                <span className="sesiones-drive-item-name">{c.name}</span>
                <ChevronRight size={16} className="sesiones-drive-item-go" />
              </button>
            ))}
        </div>

        <footer className="sesiones-drive-modal-foot">
          <p className="sesiones-drive-modal-destino">
            Se va a crear una carpeta con el nombre de la sesión dentro de{" "}
            <strong>{actual.name}</strong>.
          </p>
          <div className="sesiones-drive-modal-actions">
            <button
              type="button"
              className="sesiones-drive-btn sesiones-drive-btn--ghost"
              onClick={onClose}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="sesiones-drive-btn"
              onClick={confirmar}
              disabled={!actual.id}
              title={
                actual.id
                  ? undefined
                  : "Elegí una carpeta concreta, no la raíz de tu Drive."
              }
            >
              <Check size={16} /> Usar esta carpeta
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default DriveFolderPicker;